import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiError } from '@apptypes/index';

const WRITES_URL   = import.meta.env.VITE_WRITES_API_URL   || 'http://localhost:8080/api/v1';
const READS_URL    = import.meta.env.VITE_READS_API_URL    || 'http://localhost:8000/api/v1';
const KRA_URL      = import.meta.env.VITE_KRA_API_URL      || 'http://localhost:4001/api';
const DELIVERY_URL = import.meta.env.VITE_DELIVERY_API_URL || 'http://localhost:4000/api';

// All Spring Boot endpoints return { success: boolean, data: T, timestamp: string }.
// Unwrap so callers receive T directly.
function unwrap<T>(raw: unknown): T {
  if (raw && typeof raw === 'object' && 'success' in raw && 'data' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

// Extract a human-readable message from the API error envelope.
// Spring Boot shape: { success: false, error: { code, message, details } }
// FastAPI/fallback: { detail: string } or plain { message: string }
function extractMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const d = data as Record<string, unknown>;
  if (d.error && typeof d.error === 'object') {
    const e = d.error as Record<string, unknown>;
    if (typeof e.message === 'string') return e.message;
  }
  if (typeof d.message === 'string') return d.message;
  if (typeof d.detail === 'string')  return d.detail;
  return fallback;
}

// ── Token refresh state (shared across all clients; they share one JWT) ───
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject:  (error: unknown) => void;
}> = [];

function drainQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else       resolve(token!);
  });
  failedQueue = [];
}

// Called after a successful refresh to keep the Zustand auth store in sync
// without creating a circular import (api → authStore → authService → api).
// We update localStorage first (already done at call site), then fire a
// storage event so any store listener can pick it up.
function notifyTokenRefreshed(newToken: string) {
  // Trigger the browser storage event on this tab (not fired natively for
  // same-tab writes, so we dispatch it manually).
  window.dispatchEvent(new StorageEvent('storage', {
    key:      'auth_token',
    newValue: newToken,
    storageArea: localStorage,
  }));
}

// ── Interceptor factory: applied to every axios instance ──────────────────
function createAxiosClient(baseURL: string): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 30000,
    headers: { 'Content-Type': 'application/json' },
  });

  // ── Request: attach current JWT ─────────────────────────────────────────
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem('auth_token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // ── Response: on 401, refresh token then retry original request ─────────
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Guard: Axios can emit errors without a config (e.g. network timeout
      // before the request was even sent). Nothing to retry in that case.
      if (!error.config) {
        return Promise.reject(buildApiError(error));
      }

      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      // Only attempt refresh on 401 and only once per original request.
      if (error.response?.status !== 401 || originalRequest._retry) {
        return Promise.reject(buildApiError(error));
      }

      // ── Another refresh is already in flight: queue this request ────────
      if (isRefreshing) {
        return new Promise<unknown>((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(client(originalRequest));
            },
            reject,
          });
        });
      }

      // ── We are the one responsible for refreshing ────────────────────────
      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('auth_refresh_token');

      if (!refreshToken) {
        // No refresh token stored. User must log in again.
        isRefreshing = false;
        drainQueue(new Error('No refresh token'));
        redirectToLogin();
        return Promise.reject(buildApiError(error));
      }

      try {
        // Use a plain axios call (not a client instance) so the interceptor
        // chain does not recursively catch this 401 as well.
        const { data: raw } = await axios.post(
          `${WRITES_URL}/auth/refresh`,
          { refreshToken },
          { headers: { 'Content-Type': 'application/json' } },
        );

        const refreshed = unwrap<{ accessToken: string; refreshToken?: string }>(raw);
        const newToken   = refreshed.accessToken;

        // Persist to localStorage first; the request interceptor reads from
        // here, so all queued retries will automatically pick up the new token.
        localStorage.setItem('auth_token', newToken);
        if (refreshed.refreshToken) {
          localStorage.setItem('auth_refresh_token', refreshed.refreshToken);
        }

        // Notify any auth store listeners that the token changed.
        notifyTokenRefreshed(newToken);

        // Unblock queued requests.
        drainQueue(null, newToken);

        // Retry the original request with the new token.
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return client(originalRequest);

      } catch (refreshError) {
        // Refresh failed (expired or revoked refresh token). Log out.
        drainQueue(refreshError, null);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_refresh_token');
        localStorage.removeItem('auth_user');
        redirectToLogin();
        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    },
  );

  return client;
}

function buildApiError(error: AxiosError): ApiError {
  return {
    message: extractMessage(error.response?.data, error.message || 'An error occurred'),
    status:  error.response?.status || 0,
    errors:  (error.response?.data as any)?.error?.details,
  };
}

function redirectToLogin() {
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// ── Named axios instances ──────────────────────────────────────────────────
export const writesClient   = createAxiosClient(WRITES_URL);    // Spring Boot :8080
export const readsClient    = createAxiosClient(READS_URL);     // FastAPI     :8000
export const kraClient      = createAxiosClient(KRA_URL);       // KRA service :4001
export const deliveryClient = createAxiosClient(DELIVERY_URL);  // Delivery    :4000

// ── Typed helper wrappers ──────────────────────────────────────────────────
function makeApi(client: AxiosInstance) {
  return {
    get: async <T>(url: string, params?: Record<string, unknown>): Promise<T> => {
      const res = await client.get(url, { params });
      return unwrap<T>(res.data);
    },
    post: async <T>(url: string, data?: unknown): Promise<T> => {
      const res = await client.post(url, data);
      return unwrap<T>(res.data);
    },
    put: async <T>(url: string, data?: unknown): Promise<T> => {
      const res = await client.put(url, data);
      return unwrap<T>(res.data);
    },
    patch: async <T>(url: string, data?: unknown): Promise<T> => {
      const res = await client.patch(url, data);
      return unwrap<T>(res.data);
    },
    delete: async <T>(url: string): Promise<T> => {
      const res = await client.delete(url);
      return unwrap<T>(res.data);
    },
  };
}

export const writesApi   = makeApi(writesClient);
export const readsApi    = makeApi(readsClient);
export const kraApi      = makeApi(kraClient);
export const deliveryApi = makeApi(deliveryClient);

/**
 * Default `api` export: points to Spring Boot writes.
 * Services that need reads must explicitly import `readsApi`.
 */
export const api = writesApi;
export default writesClient;
